import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { profileEntryMessages as e } from './profileEntryMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Profile entry card; each part uses its own slice of it */
import styles from './ProfileEntryCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileCardHeadProps {
  avatarSrc: string | null;
  /** Null while the profile loads: a skeleton line takes the place of the sub line. */
  title: string;
  sub: string | null;
  ready: number | null;
  total: number;
  done: boolean;
  narrow: boolean;
  /** The buttons; on a narrow screen they go under the strip (`belowStrip`). */
  buttons: React.ReactNode;
}

/**
 * The head of the "Your profile" card (design `pc-head`): the round photo, "Your profile · for
 * you, not for one song", the title and the sub line, "N of 5 ready" with five segments (green at
 * 5 of 5), and the buttons. Below 576 px the head stacks and the buttons move under the strip.
 */
const ProfileCardHeadComponent: React.FC<ProfileCardHeadProps> = (props) => {
  const { avatarSrc, title, sub, ready, total, done, narrow, buttons } = props;
  const intl = useIntl();
  // react-intl returns the rich text as a list: the tag needs a key (QA 2026-10-24 F27).
  const bold = (chunks: React.ReactNode[]) => <b key="ready">{chunks}</b>;
  return (
    <div className={styles.head}>
      <div className={styles.avatar} aria-hidden="true">
        {avatarSrc !== null && <img src={avatarSrc} alt="" />}
      </div>
      <div className={styles.text}>
        <span className={styles.eyebrow}>
          <i className={styles.dot} aria-hidden="true" />
          {intl.formatMessage(narrow ? e.title : e.eyebrow)}
        </span>
        <h3 className={styles.title}>{title}</h3>
        {sub === null ? <span className={styles.skeleton} /> : <p className={styles.sub}>{sub}</p>}
      </div>
      <div className={styles.side}>
        <div className={styles.progress} data-testid="profile-card-progress">
          <span className={styles.progressText}>
            {ready === null
              ? intl.formatMessage(e.readyLoading, { total, b: bold })
              : intl.formatMessage(e.ready, { ready, total, b: bold })}
          </span>
          <div className={styles.segments}>
            {Array.from({ length: total }, (_, index) => (
              <i
                key={index}
                className={clsx(
                  styles.segment,
                  ready !== null && index < ready && (done ? styles.segmentDone : styles.segmentOn)
                )}
              />
            ))}
          </div>
        </div>
        {!narrow && <div className={styles.buttons}>{buttons}</div>}
      </div>
    </div>
  );
};

export const ProfileCardHead = TypedMemo(ProfileCardHeadComponent);
